import { Dimmer, DimmerDimmable, Form, List, Loader } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { ModalRecu } from '@/components/Encaissement/ModalRecu';
import { PayeurContext, RegieContext } from '@/contexts';
import { getRecu } from '@/routes';
import type { LigneRecuType, RecuEncaissementType } from '@/types';
import type { PayeurFacture } from '@/types/payeur';
import { formatMonetaire } from '@/utils';

// @ts-expect-error
import icon from '../../../assets/images/icon_recu.svg';

// import iconWhite from '../../../assets/images/icon_recu_white.svg';

const FactureAccordionContent = (props: { item: PayeurFacture }) => {
  const { item } = props;
  const { lignes: lignesFacture } = item;

  const [modalRecuOpen, setModalRecuOpen] = React.useState<number>(null);
  // Le détail du réçu sélectionné
  const [selectedRecu, setSelectedRecu] = React.useState<RecuEncaissementType>(null);
  const [loadingRecu, setLoadingRecu] = React.useState(false);

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const { getFactures } = React.useContext(PayeurContext);

  const hasRecuParMail = React.useMemo<boolean>(
    () => _.find(regies, { identifiant: selectedRegie })?.envoiRecuParMail || false,
    [selectedRegie, regies],
  );

  // Liste des reçus de la facture
  // Se trouvant dans facture.lignes.lignesRecu.recu
  const recus = React.useMemo(() => {
    const flatRecus = _.uniqBy(
      _.flatMap(_.flatMap(lignesFacture, 'lignesRecu'), 'recu'),
      'identifiant',
    ) as RecuEncaissementType[];

    // On reconstruit les reçus avec leurs lignes
    return _.map(flatRecus, (recu) => {
      const flatLignes = _.filter(_.flatMap(lignesFacture, 'lignesRecu'), {
        identifiantRecu: recu.identifiant,
      }) as LigneRecuType[];

      return { ...recu, lignes: _.map(flatLignes, (ligne) => _.omit(ligne, 'recu')) };
    });
  }, [lignesFacture]);

  // Récupère le détail du reçu sélectionné au besoin
  React.useEffect(() => {
    if (modalRecuOpen) {
      setLoadingRecu(true);

      request
        .get(getRecu(modalRecuOpen))
        .then(setSelectedRecu)
        .finally(() => setLoadingRecu(false));
    }
  }, [modalRecuOpen]);

  // Déclenche l'ouverture de la modal reçu
  const handleModalRecuOpen = React.useCallback(
    (identifiant: number) => () => {
      setModalRecuOpen(identifiant);
    },
    [],
  );

  // Ferme la modal reçu
  const handleModalRecuClose = React.useCallback(() => {
    setSelectedRecu(null);
    setModalRecuOpen(null);
  }, []);

  const handleAnnulation = React.useCallback(
    (recuAnnule: RecuEncaissementType) => {
      setSelectedRecu(recuAnnule);
      getFactures(true);
    },
    [getFactures],
  );

  return (
    <>
      <List animated selection>
        {_.map(recus, (recu) => {
          const { identifiant, numero, dateAnnulation, regisseur, regisseurAnnulation, lignes } =
            recu;

          const montantRegle = _.sumBy(lignes, 'montant');

          return (
            <List.Item
              key={`recu-${identifiant}`}
              data-testid={`bouton-voir-recu-${identifiant}`}
              as={DimmerDimmable}
              dimmed={identifiant === modalRecuOpen && loadingRecu}
              onClick={handleModalRecuOpen(identifiant)}
            >
              {identifiant === modalRecuOpen && loadingRecu && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
              <List.Icon size="large" style={{ height: 30, width: 50 }}>
                <img
                  src={icon}
                  style={{
                    height: '100%',
                    padding: '2px 1.5px 2px 2px',
                    borderRadius: 4,
                  }}
                  alt="icon_recu"
                />
              </List.Icon>
              <List.Content>
                <List.Description>
                  <Form>
                    <Form.Group fluid inline>
                      <Form.Field width={4}>
                        Reçu N°
                        {numero} du {dayjs(recu.dateCreation).format('DD MMMM YYYY')}
                      </Form.Field>
                      <Form.Field width={3}>Montant : {formatMonetaire(montantRegle)}</Form.Field>
                      <Form.Field width={9}>
                        {_.isNil(regisseur) ? (
                          'Paiement en ligne'
                        ) : (
                          <>
                            Établi par {regisseur.prenom} {regisseur.nom}
                          </>
                        )}
                        {!_.isNil(regisseurAnnulation) && (
                          <>
                            <span className="horizontal-separator" />
                            Annulé le {dayjs(dateAnnulation).format('DD MMMM YYYY')} par{' '}
                            {regisseurAnnulation?.prenom} {regisseurAnnulation?.nom}
                          </>
                        )}
                      </Form.Field>
                    </Form.Group>
                  </Form>
                </List.Description>
              </List.Content>
            </List.Item>
          );
        })}
      </List>
      {modalRecuOpen && !_.isNil(selectedRecu) && (
        <ModalRecu
          recu={selectedRecu}
          envoiRecuParMail={hasRecuParMail}
          onClose={handleModalRecuClose}
          onAnnulation={handleAnnulation}
        />
      )}
    </>
  );
};

export default FactureAccordionContent;
